import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { oauthAPI, setAuthToken, authAPI } from '../services/api';
import { toast } from 'react-toastify';
import { trackOAuth, trackError, trackAuth, setUserId } from '../utils/analytics';
import VaultoLogo from './VaultoLogo';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
    console.log('OAuthCallback component - handleCallback started');
    console.log('Current URL:', window.location.href);
    console.log('Full URL search params:', window.location.search);
    console.log('URL hash:', window.location.hash);
    console.log('User Agent:', navigator.userAgent);
    
    // Parse hash parameters for iOS Safari compatibility
    // iOS Safari can strip query params but preserves hash fragments
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    
    // Debug all URL parameters
    const allParams = {};
    searchParams.forEach((value, key) => {
      allParams[key] = value;
    });
    console.log('All URL parameters:', allParams);
    
    const allHashParams = {};
    hashParams.forEach((value, key) => {
      allHashParams[key] = value;
    });
    console.log('All hash parameters:', allHashParams);
    
    // Try to get params from hash first (iOS Safari), then fall back to query params
    const code = hashParams.get('code') || searchParams.get('code');
    const state = hashParams.get('state') || searchParams.get('state');
    const error = hashParams.get('error') || searchParams.get('error');
    const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');
    const token = hashParams.get('token') || searchParams.get('token'); // For Google Auth
    const provider = hashParams.get('provider') || searchParams.get('provider'); // For Google Auth
    const newUser = hashParams.get('new_user') || searchParams.get('new_user'); // For Google Auth
      
    // Log what we found
    console.log('Parsed parameters:', {
      code: code ? 'present' : 'missing',
      state: state ? 'present' : 'missing',
      error,
      token: token ? 'present' : 'missing',
      provider,
      newUser,
      source: hashParams.get('token') ? 'hash' : searchParams.get('token') ? 'query' : 'none'
    });

      // Check URL path to determine which provider
      const path = window.location.pathname;
      const isInstagram = path.includes('instagram');
      const isGoogle = path.includes('google') || provider === 'google';
      const isFacebook = path.includes('facebook');

      if (error) {
        // Track OAuth connection failure
        const errorMsg = errorDescription || error;
        if (isInstagram) {
          trackOAuth.connectInstagram(false);
          trackError.authError('instagram_oauth_error', errorMsg);
        } else if (isGoogle) {
          trackOAuth.connectGoogleDrive(false);
          trackError.authError('google_oauth_error', errorMsg);
        }
        
        setError(errorMsg);
        toast.error(`Connection failed: ${errorMsg}`);
        setTimeout(() => navigate('/dashboard'), 3000);
        return;
      }

      // Handle Google Auth with token
      if (token && provider === 'google') {
        console.log('Google auth callback - token received:', token);
        console.log('Provider:', provider, 'New user:', newUser);
        console.log('User Agent:', navigator.userAgent);
        console.log('Is Mobile:', /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
        
        try {
          // CRITICAL: Store token in sessionStorage FIRST (more reliable on iOS Safari)
          // iOS Safari can clear localStorage during cross-site redirects, but sessionStorage is more persistent
          sessionStorage.setItem('token', token);
          sessionStorage.setItem('oauth_token', token); // Backup key
          console.log('Token saved to sessionStorage');
          
          // Also store in localStorage (for desktop and as backup)
          setAuthToken(token);
          localStorage.setItem('token', token);
          localStorage.setItem('oauth_token', token); // Backup key
          console.log('Token saved to localStorage');
          
          // Verify storage worked (critical for iOS)
          const sessionToken = sessionStorage.getItem('token');
          const localToken = localStorage.getItem('token');
          if (!sessionToken && !localToken) {
            throw new Error('Failed to persist token - storage may be blocked');
          }
          console.log('Token verification - sessionStorage:', !!sessionToken, 'localStorage:', !!localToken);
          
          // Fetch user profile to ensure auth state is properly initialized
          // This is critical for mobile browsers where localStorage might not persist immediately
          console.log('Fetching user profile...');
          const profileResponse = await authAPI.getProfile();
          const userData = profileResponse.data;
          
          // Store user in BOTH sessionStorage and localStorage
          // sessionStorage is more reliable on iOS Safari during redirects
          sessionStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('user', JSON.stringify(userData));
          console.log('User profile saved to both storages');
          
          // Verify user storage
          const sessionUser = sessionStorage.getItem('user');
          const localUser = localStorage.getItem('user');
          console.log('User verification - sessionStorage:', !!sessionUser, 'localStorage:', !!localUser);
          
          // Set user ID for Google Analytics
          if (userData.id) {
            setUserId(userData.id);
          }
          
          // Track successful Google login/signup
          if (newUser === 'true') {
            trackAuth.signup('google');
            toast.success('Account created successfully with Google!');
          } else {
            trackAuth.login('google');
            toast.success('Signed in with Google!');
          }
          
          // Clear URL parameters to prevent issues on refresh
          // This is especially important on mobile browsers
          window.history.replaceState({}, document.title, '/oauth-callback');
          
          // Use window.location.replace to force a full page reload
          // This ensures AuthContext re-initializes with the new token/user data
          // React Router navigate doesn't re-initialize AuthContext, causing auth state to be stale
          console.log('Redirecting to dashboard...');
          setProcessing(false);
          
          // Longer delay for mobile browsers to ensure storage persistence
          // iOS Safari needs more time to persist storage before navigation
          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
          const delay = isMobile ? 800 : 300;
          
          setTimeout(() => {
            // Final verification before redirect
            const finalToken = sessionStorage.getItem('token') || localStorage.getItem('token');
            if (!finalToken) {
              console.error('Token lost before redirect - this should not happen');
              // Try to recover from URL if still available
              const urlParams = new URLSearchParams(window.location.search);
              const urlToken = urlParams.get('token');
              if (urlToken) {
                sessionStorage.setItem('token', urlToken);
                localStorage.setItem('token', urlToken);
                console.log('Recovered token from URL');
              }
            }
            
            // Check if wizard mode is pending (user came from homepage with ?wizard=true)
            const wizardModePending = localStorage.getItem('wizard_mode_pending');
            if (wizardModePending === 'true') {
              localStorage.removeItem('wizard_mode_pending');
              sessionStorage.setItem('wizard_mode_active', 'true');
            }
            
            // Check for redirect URL stored before OAuth (e.g., from affiliates page)
            const oauthRedirect = localStorage.getItem('oauth_redirect');
            if (oauthRedirect) {
              localStorage.removeItem('oauth_redirect');
              window.location.replace(oauthRedirect);
            } else {
              // Affiliates now go to regular dashboard like all other users
              window.location.replace('/dashboard');
            }
          }, delay);
          return;
        } catch (error) {
          console.error('Google auth error:', error);
          const errorMsg = error.response?.data?.detail || 'Failed to authenticate with Google';
          setError(errorMsg);
          trackError.authError('google_auth_callback_error', errorMsg);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      try {
        let response;
        
        if (isInstagram) {
          console.log('🔵 Processing Instagram callback...');
          response = await oauthAPI.handleInstagramCallback(code, state);
          console.log('✅ Instagram API callback successful');
          
          // Track successful Instagram connection
          console.log('🎯 About to track Instagram connection...');
          trackOAuth.connectInstagram(true);
          console.log('✅ Instagram tracking completed');
          
          // Check if both Instagram and Google Drive are now connected
          try {
            const oauthStatus = await oauthAPI.getStatus();
            if (oauthStatus.data?.instagram_connected && oauthStatus.data?.google_connected) {
              console.log('🎯 Both connections active - firing UserConnected event');
              trackOAuth.userConnected();
            }
          } catch (statusError) {
            console.warn('Could not check OAuth status:', statusError);
          }
          
          toast.success('Instagram connected successfully!');
        } else if (isGoogle) {
          console.log('🔵 Processing Google callback...');
          response = await oauthAPI.handleGoogleCallback(code, state);
          console.log('✅ Google API callback successful');
          
          // Track successful Google Drive connection
          console.log('🎯 About to track Google connection...');
          trackOAuth.connectGoogleDrive(true);
          console.log('✅ Google tracking completed');
          
          // Check if both Instagram and Google Drive are now connected
          try {
            const oauthStatus = await oauthAPI.getStatus();
            if (oauthStatus.data?.instagram_connected && oauthStatus.data?.google_connected) {
              console.log('🎯 Both connections active - firing UserConnected event');
              trackOAuth.userConnected();
            }
          } catch (statusError) {
            console.warn('Could not check OAuth status:', statusError);
          }
          
          toast.success('Google Drive connected successfully!');
        } else if (isFacebook) {
          console.log('🔵 Processing Facebook callback...');
          await oauthAPI.handleFacebookCallback(code, state);
          toast.success('Facebook connected successfully!');
        }

        // Redirect to dashboard after successful connection
        console.log('➡️ Redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('OAuth callback error:', error);
        const errorMsg = error.response?.data?.detail || 'Failed to complete connection';
        
        // Track connection failure
        if (isInstagram) {
          trackOAuth.connectInstagram(false);
          trackError.authError('instagram_callback_error', errorMsg);
        } else if (isGoogle) {
          trackOAuth.connectGoogleDrive(false);
          trackError.authError('google_callback_error', errorMsg);
        }
        
        setError(errorMsg);
        toast.error(errorMsg);
        setTimeout(() => navigate('/dashboard'), 3000);
      } finally {
        setProcessing(false);
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
      
      <div className="relative flex items-center justify-center min-h-screen">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl max-w-md w-full mx-4">
          <div className="text-center">
            <VaultoLogo className="h-16 w-auto mx-auto mb-6" showText={true} />
            
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Completing connection...
                </h2>
                <p className="text-gray-300 text-sm">
                  Please wait while we set up your account
                </p>
              </>
            ) : error ? (
              <>
                <div className="text-red-400 mb-4">
                  <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Connection Failed
                </h2>
                <p className="text-gray-300 text-sm mb-4">
                  {error}
                </p>
                <p className="text-gray-400 text-xs">
                  Redirecting to dashboard...
                </p>
              </>
            ) : (
              <>
                <div className="text-green-400 mb-4">
                  <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Connection Successful!
                </h2>
                <p className="text-gray-300 text-sm">
                  Redirecting to dashboard...
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
